import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { useToast } from "../lib/toast";

/**
 * Profile team hub: see the teams you're on (with your role), and join or
 * create a team inline — no detour to a separate page. "Coach" = team admin.
 */
export function TeamsHub() {
  const toast = useToast();
  const teams = useAsync(() => api.listTeams(), []);
  const [code, setCode] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const join = async () => {
    const c = code.trim();
    if (!c) return;
    setBusy(true);
    try {
      const res = await api.joinTeams([c]);
      const ok = res.find((r) => r.ok);
      if (ok) {
        toast.success("Joined team");
        setCode("");
        teams.reload();
      } else {
        toast.error(res[0]?.message ?? "That code didn't match a team.");
      }
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const n = newName.trim();
    if (!n) return;
    setBusy(true);
    try {
      const t = await api.createTeam(n);
      toast.success(`Created ${t.name} — you're the Coach`);
      setNewName("");
      teams.reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const items = teams.data?.items ?? [];

  return (
    <div className="card">
      <h2>My teams</h2>
      <p className="muted">
        Share meets, RSVPs, and lineups with your team. Join with a code from
        your coach, or create a team (you become its Coach).
      </p>

      {items.length > 0 && (
        <div className="stagger" style={{ marginBottom: "0.75rem" }}>
          {items.map((t) => (
            <div key={t.teamId} className="row spread">
              <Link to={`/teams/${t.teamId}`}>
                <strong>{t.name ?? t.teamId}</strong>
              </Link>
              <span className="badge accent">
                {t.role === "admin" ? "Coach" : "Parent"}
              </span>
            </div>
          ))}
        </div>
      )}
      {teams.data && items.length === 0 && (
        <p className="muted">You're not on a team yet.</p>
      )}

      <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
        <div className="row" style={{ gap: "0.35rem" }}>
          <input
            placeholder="Join code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            style={{ width: "9rem" }}
          />
          <button className="secondary" onClick={join} disabled={busy}>
            Join
          </button>
        </div>
        <div className="row" style={{ gap: "0.35rem" }}>
          <input
            placeholder="New team name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button className="secondary" onClick={create} disabled={busy}>
            Create team
          </button>
        </div>
      </div>
    </div>
  );
}
